import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import useCurrentUser from '~/hooks/useCurrentUser'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useGetBootcampOverviewQuery } from '~/bootcamp/apis/bootcamp.api'
import { QuizState } from '~/bootcamp/components/Quiz/QuizData.models'
import appConfig from '~/config/app'

import {
  damageBoss,
  selectBossCurrentBehavior,
  selectBossIsBusy,
  selectQuizState,
  setBossCurrentBehavior,
  setBossIsBusy,
  setBossResponse,
  setDamagePlayer,
} from '~/bootcamp/slices/bootcamp-quiz.slice'

import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'
import playCanvasAnalytics from '~/analytics/playcanvas/playcanvasAnalyticsEventLogger'

import { AppLanguage } from '~/types/locale'

import { BossBehaviors, BossMessage } from '~/bootcamp/Activities/types/activities.types'

import usePetState from '~/shared/apis/usePetState.hook'
import { useGetUserItemsQuery } from '~/redux/apis/gem-shop-api'

import BossHealthBar from '~/bootcamp/components/Quiz/BossHealthBar'
import { BossBattleEnemy } from '~/bootcamp/components/Quiz/QuizProps.models'
import { BossState } from '~/bootcamp/types/bootcamp.types'

const bossUrl = appConfig.playcanvas.bootcampBoss

export default function Boss({
  subjectName,
  className,
  setShowSplashScreen,
  bossBattleEnemy,
  showBoss,
}: {
  subjectName: string
  className?: string
  setShowSplashScreen: Dispatch<SetStateAction<boolean>>
  bossBattleEnemy: BossBattleEnemy
  showBoss: boolean
}): React.ReactElement {
  const { i18n } = useTranslation()
  const router = useRouter()
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasMinTimerPast, setHasMinTimerPast] = useState(false)
  const bossFrameRef = useRef<HTMLIFrameElement>(null)
  const quizState = useAppSelector<QuizState>(selectQuizState)

  const [petState] = usePetState()

  const dispatch = useAppDispatch()
  const bossCurrentBehavior = useAppSelector(selectBossCurrentBehavior)
  const bossIsBusy = useAppSelector(selectBossIsBusy)

  const { key: bootcampKey, moduleKey } = router.query
  const user = useCurrentUser()
  const { data } = useGetBootcampOverviewQuery(
    { key: bootcampKey as string, language: i18n.language, userId: user?.id ?? -1 },
    { skip: !user },
  )
  const userItems = useGetUserItemsQuery(undefined, { skip: !user?.id })

  const messageBoss = useCallback((message: BossMessage): void => {
    const postMessage = bossFrameRef.current?.contentWindow
    if (message.action && message.action !== BossBehaviors.MUTE && message.action !== BossBehaviors.UNMUTE) {
      if (!bossFrameRef.current) return
      bossFrameRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
    }
    if (postMessage) {
      postMessage.postMessage(message, bossUrl.origin)
    }
  }, [])
  const { data: metadata, isSuccess: hasLoadedMetadata } = useGetAvatarMetadataQuery(user, { skip: !user?.avatarUrl })

  useEffect(() => {
    if (isLoaded && petState && userItems.isSuccess) {
      messageBoss({ type: 'petData', petState, userItems: userItems.data })
    }
  }, [isLoaded, petState, userItems, messageBoss])

  useEffect(() => {
    if (isLoaded && hasMinTimerPast) {
      setTimeout(() => {
        setShowSplashScreen(false)
      }, 1000)
    } else {
      setShowSplashScreen(true)
    }
  }, [isLoaded, hasMinTimerPast, setShowSplashScreen])

  useEffect(() => {
    setTimeout(() => {
      setHasMinTimerPast(true)
    }, 3000)
  }, [])

  useEffect(() => {
    const module = data?.modules?.find((object) => object.moduleNumber === moduleKey)
    const defeatedBosses = data?.modules?.filter((object) => object.bossDefeated).map((object) => object.moduleNumber)
    if (!data || !isLoaded || !module || !quizState.mathEnergyEarned || !bootcampKey) return
    messageBoss({
      reward: {
        subject: bootcampKey as string,
        currentBootcamp: moduleKey as string,
        kudos: quizState.mathEnergyEarned,
        hasBadge: module.bossDefeated,
        defeatedBosses: defeatedBosses ?? [],
      },
    })
  }, [isLoaded, data, moduleKey, messageBoss, quizState.mathEnergyEarned, bootcampKey])

  const avatarMessage = useCallback((): void => {
    const postMessage = bossFrameRef.current?.contentWindow

    if (!bootcampKey) return

    try {
      if (postMessage) {
        postMessage.postMessage(
          {
            metaData: metadata ?? 'defualt',
            bossBattleEnemy: bossBattleEnemy ?? 'defualt',
            name: subjectName,
            bootcampKey,
          },
          bossUrl.origin,
        )
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log('error fetching metadata', e, user?.avatarUrl, user?.id)
    }
  }, [user, metadata, bossBattleEnemy, subjectName, bootcampKey])

  useEffect(() => {
    if (isLoaded) avatarMessage()
  }, [hasLoadedMetadata, isLoaded, avatarMessage])

  const messageHandler = useCallback(
    (event: MessageEvent) => {
      // check that the message is coming from playcanvas
      if (event.origin === bossUrl.origin) {
        playCanvasAnalytics.logPlayCanvasEvent(event)
        const messageData = event.data

        if (messageData.state === BossState.READY && !messageData.anim) {
          messageBoss({ type: 'language', language: i18n.language as AppLanguage })
          setIsLoaded(true)
        }

        if (messageData.state === BossState.DAMAGE_BOSS) {
          dispatch(damageBoss())
        } else if (messageData.state === BossState.DAMAGE_PLAYER) {
          dispatch(setDamagePlayer(true))
        }

        if (messageData.anim) {
          dispatch(setBossResponse(messageData))
        }
      }
    },
    [dispatch, i18n.language, messageBoss],
  )

  useEffect(() => {
    window.addEventListener('message', messageHandler)
    if (!isLoaded) messageBoss({ type: 'ready' })

    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler, messageBoss, isLoaded])

  useEffect(() => {
    if (bossCurrentBehavior) {
      switch (bossCurrentBehavior) {
        case BossBehaviors.INTRO:
          messageBoss({ action: BossBehaviors.INTRO })
          break

        case BossBehaviors.RIGHT:
          dispatch(setBossIsBusy(true))
          messageBoss({ action: BossBehaviors.RIGHT })
          dispatch(setBossCurrentBehavior(null))
          break

        case BossBehaviors.WRONG:
          dispatch(setBossIsBusy(true))
          messageBoss({ action: BossBehaviors.WRONG })
          dispatch(setBossCurrentBehavior(null))
          break

        case BossBehaviors.VICTORY:
          dispatch(setBossIsBusy(true))
          messageBoss({ action: BossBehaviors.VICTORY })
          dispatch(setBossCurrentBehavior(null))
          break

        case BossBehaviors.DEFEAT:
          dispatch(setBossIsBusy(true))
          messageBoss({ action: BossBehaviors.DEFEAT })
          dispatch(setBossCurrentBehavior(null))
          break

        case BossBehaviors.RESTART:
          messageBoss({ action: BossBehaviors.RESTART })
          dispatch(setBossCurrentBehavior(null))
          break

        case BossBehaviors.RELOAD:
          messageBoss({ action: BossBehaviors.RELOAD })
          dispatch(setBossCurrentBehavior(null))
          break

        case BossBehaviors.MUTE:
          messageBoss({ action: BossBehaviors.MUTE })
          break

        case BossBehaviors.UNMUTE:
          messageBoss({ action: BossBehaviors.UNMUTE })
          break

        default:
          dispatch(setBossIsBusy(false))
          break
      }
    }
  }, [bossCurrentBehavior, messageBoss, dispatch])

  useEffect(() => {
    if (!bossIsBusy) {
      dispatch(setBossCurrentBehavior(null))
    }
  }, [bossIsBusy, dispatch])

  return (
    <div className={className}>
      <div className="relative h-0 w-full flex justify-center">
        <BossHealthBar className="absolute top-4" />
      </div>

      <iframe
        className={`select-none w-full min-h-sm bossFrame rounded-b -z-1 ${showBoss ? '' : 'pointer-events-none'}`}
        ref={bossFrameRef}
        src={bossUrl.href}
        style={{ transform: 'none !important', filter: 'none !important', willChange: 'auto !important' }}
      />
    </div>
  )
}
